import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Spinner } from "reactstrap";
import { IoClose } from "react-icons/io5";
import { apiAdmin } from "../../services/api";
import { _imagesURL } from "../../constants/API";
import { NEW_PROFILE_DOCTOR } from "../../constants/RoutesUrl";
import { Language } from "../../App";
import { resolveMentorImage, DEFAULT_MENTOR_AVATAR as placeholderAvatar } from "../../utils/mentorshipUtils";
import { isImageCached, markImageLoaded } from "../../utils/imageCache";

// Must match the .mml-curtain-content "mmlCurtainOpen" animation's own
// duration below — same pattern as ApplyMentorShip.jsx's OPEN_ANIMATION_MS:
// the mentees request only fires once the curtain has actually finished
// sliding down and settled in the center, not while still mid-animation.
const OPEN_ANIMATION_MS = 800;

// Laravel-paginated: res.data.data is the page meta (current_page,
// last_page, ...) and res.data.data.data is the actual array of mentees —
// same shape WebinarInvited.jsx's handleInvitationList already reads.
async function fetchMentees(page = 1) {
  const res = await apiAdmin.get("user/mentor/mentees", { params: { page } });
  return res.data;
}

// The API row may either be the user object directly, or wrap it under
// `.user` (same shape MentorshipRequests.jsx's mapActivity already handles
// for activity.user/activity.mentor) — this covers both without caring
// which one it turns out to be.
function getMenteeUser(mentee) {
  return mentee?.user || mentee;
}

function MenteeRow({ mentee, onClick }) {
  const user = getMenteeUser(mentee);
  const image = resolveMentorImage(user);

  const name =
    user?.name ||
    [user?.firstname, user?.lastname].filter(Boolean).join(" ") ||
    "";
  const subtitle =
    user?.mentor_profile?.specialty ||
    user?.doctor_profile?.experience ||
    user?.category ||
    user?.subcategory ||
    "";
  const [photoLoaded, setPhotoLoaded] = useState(() => isImageCached(image));
  const imgRef = useRef(null);

  useEffect(() => {
    if (isImageCached(image)) {
      setPhotoLoaded(true);
    } else if (imgRef.current?.complete && imgRef.current?.naturalWidth > 0) {
      markImageLoaded(image);
      setPhotoLoaded(true);
    } else {
      setPhotoLoaded(false);
    }
  }, [image]);

  return (
    <div
      onClick={onClick}
      className="d-flex align-items-center gap-3 p-2"
      style={{
        borderRadius: "14px",
        cursor: "pointer",
        transition: "background 0.15s ease",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "#F7FDFF")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
    >
      <div
        className="d-flex align-items-center justify-content-center flex-shrink-0"
        style={{
          position: "relative",
          width: "48px",
          height: "48px",
          borderRadius: "50%",
          overflow: "hidden",
          background: "#F7FDFF",
        }}
      >
        {!photoLoaded && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1,
            }}
          >
            <Spinner size="sm" style={{ color: "#1dc1dd" }} />
          </div>
        )}
        <img
          ref={imgRef}
          src={image}
          alt={name}
          loading="eager"
          decoding="async"
          onLoad={() => {
            markImageLoaded(image);
            setPhotoLoaded(true);
          }}
          onError={(e) => {
            markImageLoaded(image);
            setPhotoLoaded(true);
            if (e.target.src !== placeholderAvatar) {
              e.target.src = placeholderAvatar;
            }
          }}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            opacity: photoLoaded ? 1 : 0,
            transition: photoLoaded ? "none" : "opacity 0.25s ease-out",
            willChange: "opacity",
          }}
        />
      </div>
      <div className="flex-grow-1 overflow-hidden">
        <span
          className="fs-14 d-block text-truncate"
          style={{ color: "#0E1421", fontWeight: 600 }}
        >
          {name}
        </span>
        {subtitle && (
          <span
            className="fs-12 d-block text-truncate"
            style={{ color: "#5F6673" }}
          >
            {subtitle}
          </span>
        )}
      </div>
    </div>
  );
}

// Mentee list shown from MentorshipStats.jsx's "Mentees" card — same
// curtain-modal open/close animation and loader-while-fetching pattern as
// ApplyMentorShip.jsx, just listing user/mentor/mentees instead of a single
// mentor's profile. Clicking a row navigates to that mentee's profile.
function MentorMenteesModal({ isOpen, toggle }) {
  const navigate = useNavigate();
  const { lang } = useContext(Language);
  const [mentees, setMentees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  // Pagination meta straight off the API response (current_page, last_page,
  // ...) — same fields WebinarInvited.jsx's inviteData already reads off.
  const [pageInfo, setPageInfo] = useState(null);
  // False for the entire slide-down animation (the request hasn't even been
  // fired yet) — same reasoning as ApplyMentorShip.jsx's fetchStarted.
  const [fetchStarted, setFetchStarted] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setMentees([]);
      setLoading(false);
      setLoadingMore(false);
      setPageInfo(null);
      setFetchStarted(false);
      return undefined;
    }
    let cancelled = false;
    setMentees([]);
    setLoading(false);
    setLoadingMore(false);
    setPageInfo(null);
    setFetchStarted(false);

    const openTimer = setTimeout(() => {
      if (cancelled) return;
      setFetchStarted(true);
      setLoading(true);
      fetchMentees(1)
        .then((data) => {
          if (cancelled || !data?.status) return;
          setMentees(Array.isArray(data?.data?.data) ? data.data.data : []);
          setPageInfo(data?.data || null);
        })
        .catch(() => {})
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, OPEN_ANIMATION_MS);

    return () => {
      cancelled = true;
      clearTimeout(openTimer);
    };
  }, [isOpen]);

  const hasMore = pageInfo && pageInfo.current_page < pageInfo.last_page;

  const handleLoadMore = () => {
    if (!hasMore || loadingMore) return;
    setLoadingMore(true);
    fetchMentees(pageInfo.current_page + 1)
      .then((data) => {
        if (!data?.status) return;
        setMentees((prev) => [
          ...prev,
          ...(Array.isArray(data?.data?.data) ? data.data.data : []),
        ]);
        setPageInfo(data?.data || null);
      })
      .catch(() => {})
      .finally(() => setLoadingMore(false));
  };

  // Same curtain-modal pattern as ApplyMentorShip.jsx — a plain backdrop +
  // content div pair driven by CSS @keyframes, not reactstrap's Modal/Fade.
  // isClosing keeps both mounted for the close animation's own 800ms before
  // actually telling the parent to close (toggle).
  const closeThen = (callback) => {
    setIsClosing(true);
    setTimeout(() => {
      toggle();
      setIsClosing(false);
      callback?.();
    }, 800);
  };
  const handleClose = () => closeThen();

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") handleClose();
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen && !isClosing) return null;

  const handleMenteeClick = (mentee) => {
    const user = getMenteeUser(mentee);
    const identifier = user?.user_name || user?.id;
    if (!identifier) return;
    closeThen(() => navigate(`${NEW_PROFILE_DOCTOR}/${identifier}`));
  };

  return (
    <div
      className={`mml-curtain-backdrop ${isClosing ? "closing" : ""}`}
      onClick={handleClose}
    >
      <style>{`
        /* Curtain modal — same pattern as ApplyMentorShip.jsx's
           .am-curtain-backdrop/.am-curtain-content: a plain backdrop +
           content div pair animated with @keyframes, not reactstrap's
           Modal/Fade, so open and close are both immediate and symmetric. */
        .mml-curtain-backdrop {
          position: fixed;
          inset: 0;
          background-color: rgba(0, 0, 0, 0.5);
          z-index: 1050;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          animation: mmlBackdropFadeIn 0.8s ease-out forwards;
        }
        .mml-curtain-backdrop.closing {
          animation: mmlBackdropFadeOut 0.8s ease-out forwards;
        }
        .mml-curtain-content {
          position: relative;
          width: 100%;
          max-width: 480px;
          max-height: 85vh;
          overflow-y: auto;
          background: #fff;
          border: 0.5px solid #E6E7EA;
          border-radius: 26px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.05);
          transform: translateY(-100vh);
          animation: mmlCurtainOpen 0.8s ease-out forwards;
        }
        .mml-curtain-content.closing {
          animation: mmlCurtainClose 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
        }
        @keyframes mmlBackdropFadeIn {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }
        @keyframes mmlBackdropFadeOut {
          0% { opacity: 1; }
          100% { opacity: 0; }
        }
        @keyframes mmlCurtainOpen {
          0% { transform: translateY(-100vh); }
          100% { transform: translateY(0); }
        }
        @keyframes mmlCurtainClose {
          0% { transform: translateY(0); }
          100% { transform: translateY(-100vh); }
        }
        @media (prefers-reduced-motion: reduce) {
          .mml-curtain-backdrop, .mml-curtain-content {
            animation: none !important;
          }
        }
      `}</style>
      <div
        className={`mml-curtain-content ${isClosing ? "closing" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="position-relative" style={{ padding: "22px" }}>
          <button
            type="button"
            onClick={handleClose}
            className="position-absolute d-flex align-items-center justify-content-center border-0"
            style={{
              top: "16px",
              right: "16px",
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              background: "#fff",
              boxShadow: "0 2px 8px rgba(15,26,43,0.16)",
              zIndex: 1,
            }}
          >
            <IoClose size={18} color="#0E1421" />
          </button>

          <h5
            className="mb-3"
            style={{ fontSize: "18px", fontWeight: 700, color: "#0E1421" }}
          >
            {lang?.mentorship_mentees_label || "Mentees"}
          </h5>

          {!fetchStarted ? (
            // Curtain is still sliding down — nothing fetched yet.
            <div style={{ minHeight: "260px" }} />
          ) : loading ? (
            <div
              className="d-flex align-items-center justify-content-center"
              style={{ minHeight: "260px" }}
            >
              <Spinner
                style={{ color: "#1dc1dd", width: "2.5rem", height: "2.5rem" }}
              />
            </div>
          ) : mentees.length ? (
            <div className="d-flex flex-column" style={{ gap: "2px" }}>
              {mentees.map((mentee, idx) => (
                <MenteeRow
                  key={getMenteeUser(mentee)?.id || idx}
                  mentee={mentee}
                  onClick={() => handleMenteeClick(mentee)}
                />
              ))}
              {hasMore && (
                <div className="text-center mt-2">
                  <button
                    type="button"
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    className="border-0"
                    style={{
                      background: "#EAF8FB",
                      color: "#00A9C7",
                      borderRadius: "999px",
                      padding: "8px 20px",
                      fontSize: "13px",
                      fontWeight: 600,
                      cursor: loadingMore ? "default" : "pointer",
                    }}
                  >
                    {loadingMore ? (
                      <Spinner size="sm" style={{ color: "#00A9C7" }} />
                    ) : (
                      lang?.load_more || "Load More"
                    )}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <p
              className="text-center mb-0"
              style={{
                color: "#5F6673",
                fontSize: "13px",
                padding: "40px 0",
              }}
            >
              {lang?.mentorship_no_mentees_yet || "No mentees yet."}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default React.memo(MentorMenteesModal);
